import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquare, Send, Bell, Phone, Clock, Calendar, Shield } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

const RESOURCE_TYPES = ['podcast', 'article', 'event', 'video', 'job'];

const CoachMemoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    consent: false,
    timezone: 'UTC',
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    max_messages_per_day: 3,
    max_messages_per_week: 10,
    resource_types: ['podcast', 'article', 'event', 'video', 'job'],
  });
  const [memory, setMemory] = useState({});
  const [messages, setMessages] = useState([]);
  const [scheduled, setScheduled] = useState([]);

  const [phoneInput, setPhoneInput] = useState('');
  const [verifyStep, setVerifyStep] = useState('input');
  const [verificationId, setVerificationId] = useState(null);
  const [codeInput, setCodeInput] = useState('');

  const [simulateSender, setSimulateSender] = useState('+14155551234');
  const [simulateContent, setSimulateContent] = useState('');

  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user]);

  const getToken = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token;
  };

  const refresh = async () => {
    setLoading(true);
    await Promise.all([loadPreferences(), loadMemory(), loadHistory(), loadScheduled()]);
    setLoading(false);
  };

  const loadPreferences = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load preferences');
      const data = await res.json();
      setPreferences(prev => ({ ...prev, ...(data.preferences || {}) }));
    } catch (e) {
      console.error(e);
      toast.error('Could not load messaging preferences');
    }
  };

  const loadMemory = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/memory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load memory');
      const data = await res.json();
      setMemory(data.memory || {});
    } catch (e) {
      console.error(e);
    }
  };

  const loadHistory = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/history?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load history');
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadScheduled = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/scheduled`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load scheduled');
      const data = await res.json();
      setScheduled(data.checkins || []);
    } catch (e) {
      console.error(e);
    }
  };

  const savePreferences = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(preferences),
      });
      if (!res.ok) throw new Error('Failed to save preferences');
      toast.success('Preferences saved');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveMemory = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/memory`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ memory }),
      });
      if (!res.ok) throw new Error('Failed to save memory');
      toast.success('Memory saved');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const sendVerificationCode = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phone_number: phoneInput }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send code');
      setVerificationId(data.verification_id);
      setVerifyStep('code');
      if (data.code) toast.info(`Dev code: ${data.code}`);
      toast.success('Verification code sent');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const verifyCode = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/messaging/phone/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ verification_id: verificationId, code: codeInput }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      toast.success('Phone verified');
      setVerifyStep('done');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const simulateInbound = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/messaging/inbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender: simulateSender, content: simulateContent }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Simulation failed');
      toast.success(`Inbound handled: ${data.reply || 'no reply'}`);
      setTimeout(() => loadHistory(), 500);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const runScheduler = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/messaging/scheduler/run`, {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Scheduler failed');
      toast.success(`Scheduler ran: ${data.sent || 0} sent, ${data.scheduled || 0} scheduled`);
      loadScheduled();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleResourceType = (type) => {
    setPreferences(prev => ({
      ...prev,
      resource_types: prev.resource_types.includes(type)
        ? prev.resource_types.filter(t => t !== type)
        : [...prev.resource_types, type],
    }));
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pb-24" style={{ position: 'relative', fontFamily: "'Inter', sans-serif" }}>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: 'url(/bluewhite.png)', backgroundSize: 'cover', backgroundPosition: 'center',
        zIndex: -1
      }} />

      <header className="border-b fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{
        borderColor: 'rgba(255,255,255,0.3)', background: 'rgba(255,252,248,0.65)'
      }}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/roadmap')} style={{ color: '#2d1b0e' }}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#2d1b0e' }}>
              <MessageSquare className="w-5 h-5" />
              Fig Coach Messages
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 pt-28 max-w-4xl space-y-6">
        {/* Phone & consent */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Phone className="w-5 h-5" /> Phone & consent</CardTitle>
            <CardDescription>Connect your phone to receive career-coach texts from Fig.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {verifyStep === 'input' && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Input placeholder="+1 415 555 1234" value={phoneInput} onChange={e => setPhoneInput(e.target.value)} />
                <Button onClick={sendVerificationCode}>Send code</Button>
              </div>
            )}
            {verifyStep === 'code' && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Input placeholder="123456" value={codeInput} onChange={e => setCodeInput(e.target.value)} />
                <Button onClick={verifyCode}>Verify</Button>
              </div>
            )}
            {verifyStep === 'done' && (
              <p className="text-green-700 text-sm">Phone verified. You can update preferences below.</p>
            )}
            <div className="flex items-center gap-3 border-t pt-4">
              <Switch checked={preferences.consent} onCheckedChange={v => setPreferences(p => ({ ...p, consent: v }))} id="consent" />
              <label htmlFor="consent" className="text-sm">
                I consent to receive proactive career coaching messages from Fig.
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> Messaging preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Timezone</label>
                <Input value={preferences.timezone} onChange={e => setPreferences(p => ({ ...p, timezone: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4" /> Quiet hours</label>
                <div className="flex gap-2">
                  <Input value={preferences.quiet_hours_start} onChange={e => setPreferences(p => ({ ...p, quiet_hours_start: e.target.value }))} placeholder="22:00" />
                  <span className="self-center">to</span>
                  <Input value={preferences.quiet_hours_end} onChange={e => setPreferences(p => ({ ...p, quiet_hours_end: e.target.value }))} placeholder="08:00" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Max messages / day</label>
                <Input type="number" value={preferences.max_messages_per_day} onChange={e => setPreferences(p => ({ ...p, max_messages_per_day: Number(e.target.value) }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Max messages / week</label>
                <Input type="number" value={preferences.max_messages_per_week} onChange={e => setPreferences(p => ({ ...p, max_messages_per_week: Number(e.target.value) }))} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Resource types</label>
              <div className="flex flex-wrap gap-2">
                {RESOURCE_TYPES.map(type => (
                  <label key={type} className="flex items-center gap-1 text-sm border rounded-full px-3 py-1 cursor-pointer hover:bg-white/50">
                    <input
                      type="checkbox"
                      checked={preferences.resource_types.includes(type)}
                      onChange={() => toggleResourceType(type)}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={savePreferences}>Save preferences</Button>
          </CardContent>
        </Card>

        {/* Conversation history */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Conversation history</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-80 overflow-y-auto space-y-2 pr-2">
              {messages.length === 0 && <p className="text-sm text-muted-foreground">No messages yet.</p>}
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.direction === 'inbound' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.direction === 'inbound' ? 'bg-white border' : 'bg-indigo-600 text-white'}`}>
                    {m.content}
                    <div className="text-[10px] opacity-70 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Memory management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Coach memory</CardTitle>
            <CardDescription>Structured facts Fig has learned from your texts. Edit with care.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              className="w-full h-64 rounded-md border p-3 font-mono text-sm bg-white/70"
              value={JSON.stringify(memory, null, 2)}
              onChange={e => { try { setMemory(JSON.parse(e.target.value)); } catch {} }}
              onBlur={e => { try { setMemory(JSON.parse(e.target.value)); } catch { toast.error('Invalid JSON'); } }}
            />
            <Button onClick={saveMemory}>Save memory</Button>
          </CardContent>
        </Card>

        {/* Scheduled */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Scheduled check-ins</CardTitle>
          </CardHeader>
          <CardContent>
            {scheduled.length === 0 && <p className="text-sm text-muted-foreground">No upcoming check-ins.</p>}
            <ul className="space-y-2">
              {scheduled.map(c => (
                <li key={c.id} className="text-sm border rounded-lg px-3 py-2 bg-white/50 flex justify-between items-center">
                  <span>{c.reason} — {new Date(c.send_at).toLocaleString()}</span>
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">{c.status}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex gap-3">
              <Button variant="outline" onClick={runScheduler}>Run scheduler now</Button>
            </div>
          </CardContent>
        </Card>

        {/* Dev simulate */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Send className="w-5 h-5" /> Dev simulate inbound</CardTitle>
            <CardDescription>Send a fake inbound SMS to test the webhook locally.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Sender phone" value={simulateSender} onChange={e => setSimulateSender(e.target.value)} />
            <Input placeholder="Message body" value={simulateContent} onChange={e => setSimulateContent(e.target.value)} />
            <Button onClick={simulateInbound}>Simulate inbound</Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CoachMemoryPage;
